import { JSONCompatible, JSONValue, toSerializableObject } from '@direct-frontend/stdlib';

import { ErrorOptions } from '../types/ErrorOptions';
import { HeadersInput, HeadersObject, headersToObject } from './helpers/headersToObject';
import { BaseError } from './BaseError';
import { createErrorMarker } from './helpers/createErrorMarker';
import { createErrorInstanceOf } from './helpers/createErrorInstanceOf';
import { sanitizeHttpHeaders } from './helpers/sanitizeHttpHeaders';

// опции для передачи в конструктор ошибки RequestError
export type RequestErrorOptions<
    Additional extends JSONCompatible<Additional>,
    GroupBy extends Record<string, string> | undefined = Record<string, string> | undefined,
> = ErrorOptions<Additional, GroupBy> & {
    /** Параметры запроса */
    request: {
        method?: ErrorRequest['method'];
        url: ErrorRequest['url'];
        headers?: HeadersInput;
        cache?: ErrorRequest['cache'];
        credentials?: ErrorRequest['credentials'];
        mode?: ErrorRequest['mode'];
        redirect?: ErrorRequest['redirect'];
    };

    /** Данные передаваемые в запросе */
    requestData?: unknown;

    /** Ответ на запрос */
    response:
        | {
              status: ErrorResponse['status'];
              headers: HeadersInput;
          }
        | undefined;

    /** Тело ответа запроса */
    responseData?: unknown;

    /** Переопределение стандартного сообщения об ошибке */
    message?: string;

    /**
     * Имена заголовков в запросе/ответе, которые безопасно добавлять в лог.
     * Предполагается, что любая ошибка рано или поздно попадает в лог, поэтому все остальные
     * заголовки будут удалены из объектов запроса/ответа.
     */
    headersWithoutSensitiveData?: Set<string>;

    /**
     * Имена кук в заголовках запроса/ответа, которые безопасно добавлять в лог.
     * Предполагается, что любая ошибка рано или поздно попадает в лог, поэтому все остальные
     * куки будут удалены из заголовков запроса/ответа.
     */
    cookiesWithoutSensitiveData?: Set<string>;
};

// Параметры запроса. Структурно подходит для передачи стандартного объекта Request и параметров из modules-request.
export interface ErrorRequest {
    readonly method: Request['method'];
    readonly url: Request['url'];
    readonly headers: HeadersObject;
    readonly cache: Request['cache'] | undefined;
    readonly credentials: Request['credentials'] | undefined;
    readonly mode: Request['mode'] | undefined;
    readonly redirect: Request['redirect'] | undefined;
}

// Ответ на запрос. Структурно подходит для передачи стандатрного объекта Response и расширенного из modules-request.
export interface ErrorResponse {
    readonly status: Response['status'];
    readonly headers: HeadersObject;
}

export class RequestError<
    Additional extends JSONCompatible<Additional> = JSONValue,
    GroupBy extends Record<string, string> | undefined = Record<string, string> | undefined,
> extends BaseError<Additional, GroupBy> {
    // Имя конструктора(класса) ошибки. Нужно указывать для обхода минификации.
    public name: string = 'RequestError';

    // параметры запроса, передаваемые в fetch/modules-request
    public readonly request: JSONCompatible<ErrorRequest>;

    // тело запроса
    public readonly requestData: JSONValue;

    // ответ от fetch/modules-request
    // ответа может не быть, например если сеть была недоступна
    public readonly response: JSONCompatible<ErrorResponse> | undefined;

    // тело ответа
    public readonly responseData: JSONValue;

    // Конструктор ошибки
    // message генерируется автоматически по переданному request
    public constructor(options: RequestErrorOptions<Additional, GroupBy>) {
        const {
            request,
            requestData,
            response,
            responseData,
            message,
            headersWithoutSensitiveData,
            cookiesWithoutSensitiveData,
        } = options;
        const method = request.method ?? 'GET';
        const defaultMesasge = `Failed to ${method} ${removeUrlQueryAndHash(request.url)}`;

        super(message ?? defaultMesasge, options);

        Object.assign(this, { [createErrorMarker('RequestError')]: true });

        this.request = {
            method,
            url: request.url,
            headers: request.headers
                ? sanitizeHttpHeaders(headersToObject(request.headers), {
                      headersWithoutSensitiveData,
                      cookiesWithoutSensitiveData,
                  })
                : {},
            mode: request.mode,
            cache: request.cache,
            credentials: request.credentials,
            redirect: request.redirect,
        };

        this.requestData = toSerializableObject(requestData);

        this.response = response && {
            status: response.status,
            headers: sanitizeHttpHeaders(headersToObject(response.headers), {
                headersWithoutSensitiveData,
                cookiesWithoutSensitiveData,
            }),
        };

        this.responseData = toSerializableObject(responseData);
    }
}

Object.defineProperty(RequestError, Symbol.hasInstance, {
    value: createErrorInstanceOf('RequestError'),
    configurable: true,
});

function removeUrlQueryAndHash(url: string) {
    return url.replace(/(\?|\#).+$/, '');
}
